import { useState, useCallback, useEffect } from 'react';
import { 
  // eslint-disable-next-line no-unused-vars
  validateField, 
  validateForm, 
  getValidationAttributes, 
  getErrorAttributes, 
  getSuccessAttributes,
  sanitizeInput 
} from '../utils/validation';

/**
 * Custom hook for form validation
 * @param {object} initialData - Initial form data
 * @param {object} validationSchema - Validation rules for each field
 * @param {object} options - Additional options
 * @returns {object} - Form validation state and methods
 */
export const useFormValidation = (initialData, validationSchema, options = {}) => {
  const {
    validateOnBlur = false, // Changed default to false
    validateOnChange = false,
    validateOnSubmit = true,
    sanitizeOnChange = true
  } = options;

  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValid, setIsValid] = useState(false);

  // Validate a single field
  const validateSingleField = useCallback((fieldName, value) => {
    const fieldRules = validationSchema[fieldName];
    if (!fieldRules) return null;

    return validateField(value, fieldRules, formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationSchema, formData]);

  // Validate entire form
  const validateAllFields = useCallback(() => {
    const newErrors = validateForm(formData, validationSchema);
    setErrors(newErrors);
    setIsValid(Object.keys(newErrors).length === 0);
    return newErrors;
  }, [formData, validationSchema]);

  // Handle input change
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    
    // Sanitize input if enabled (but don't trim during typing)
    const sanitizedValue = sanitizeOnChange && type !== 'checkbox' 
      ? sanitizeInput(fieldValue, { maxLength: validationSchema[name]?.maxLength, trim: false })
      : fieldValue;

    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Validate on change if enabled
    if (validateOnChange) {
      const error = validateSingleField(name, sanitizedValue);
      if (error) {
        setErrors(prev => ({
          ...prev,
          [name]: error
        }));
        // Mark field as touched when there's an error
        setTouched(prev => ({
          ...prev,
          [name]: true
        }));
      }
    }
  }, [errors, validateOnChange, validateSingleField, sanitizeOnChange, validationSchema]);

  // Handle field blur
  const handleFieldBlur = useCallback((e) => {
    const { name, value } = e.target;
    
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    if (validateOnBlur) {
      const error = validateSingleField(name, value);
      if (error) {
        setErrors(prev => ({
          ...prev,
          [name]: error
        }));
      }
    }
  }, [validateOnBlur, validateSingleField]);

  // Handle form submission
  const handleSubmit = useCallback((onSubmit) => async (e) => {
    e.preventDefault();
    
    if (validateOnSubmit) {
      const newErrors = validateAllFields();
      if (Object.keys(newErrors).length > 0) {
        // Mark all fields as touched to show errors
        const allTouched = {};
        Object.keys(validationSchema).forEach(fieldName => {
          allTouched[fieldName] = true;
        });
        setTouched(allTouched);
        return;
      }
    }

    // Call the submit handler if form is valid
    if (onSubmit) {
      await onSubmit(formData);
    }
  }, [validateOnSubmit, validateAllFields, validationSchema, formData]);

  // Reset form
  const resetForm = useCallback((newData = initialData) => {
    setFormData(newData);
    setErrors({});
    setTouched({});
    setIsValid(false);
  }, [initialData]);

  // Set field value programmatically
  const setFieldValue = useCallback((fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  }, []);

  // Set field error programmatically
  const setFieldError = useCallback((fieldName, error) => {
    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
    // Mark field as touched when setting an error
    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  }, []);

  // Get validation attributes for a field
  const getFieldValidationAttributes = useCallback((fieldName) => {
    const fieldRules = validationSchema[fieldName];
    if (!fieldRules) return {};

    const attributes = getValidationAttributes(fieldRules);
    const errorMessage = errors[fieldName];
    
    if (errorMessage) {
      Object.assign(attributes, getErrorAttributes(fieldName, errorMessage));
    } else if (touched[fieldName]) {
      Object.assign(attributes, getSuccessAttributes(fieldName));
    }

    return attributes;
  }, [validationSchema, errors, touched]);

  // Check if field has error
  const hasError = useCallback((fieldName) => {
    return Boolean(errors[fieldName]);
  }, [errors]);

  // Check if field has been touched
  const hasBeenTouched = useCallback((fieldName) => {
    return Boolean(touched[fieldName]);
  }, [touched]);

  // Get field error message
  const getFieldError = useCallback((fieldName) => {
    return errors[fieldName] || '';
  }, [errors]);

  // Validate specific field
  const validateField = useCallback((fieldName) => {
    const error = validateSingleField(fieldName, formData[fieldName]);
    if (error) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: error
      }));
      // Mark field as touched when there's an error
      setTouched(prev => ({
        ...prev,
        [fieldName]: true
      }));
    }
    return error;
  }, [validateSingleField, formData]);

  // Update validation schema
  const updateValidationSchema = useCallback((newSchema) => {
    // Re-validate with new schema
    const newErrors = validateForm(formData, newSchema);
    setErrors(newErrors);
    setIsValid(Object.keys(newErrors).length === 0);
  }, [formData]);

  // Effect to update form validity when errors change
  useEffect(() => {
    setIsValid(Object.keys(errors).length === 0);
  }, [errors]);

  return {
    // State
    formData,
    errors,
    touched,
    isValid,
    
    // Methods
    handleInputChange,
    handleFieldBlur,
    handleSubmit,
    resetForm,
    setFieldValue,
    setFieldError,
    validateField,
    updateValidationSchema,
    
    // Utilities
    getFieldValidationAttributes,
    hasError,
    hasBeenTouched,
    getFieldError
  };
}; 